import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Environment as environment } from "../../environment.ts";

export interface CatalogProduct {
    productId: string;
    productName: string;
    retailerId: string;
    description: string;
    price: string;
    currency?: string;
    availability: string;
    imageUrl: string;
    purchaseUrl?: string;
}

interface CatalogSummary {
    id: string;
    name: string;
    metaProducts: RawMetaProduct[];
}

interface RawMetaProduct {
    id?: string;
    productId?: string;
    retailerId?: string;
    name?: string;
    description?: string;
    price?: string;
    currency?: string;
    availability?: string;
    imageUrl?: string;
    purchaseUrl?: string;
}

interface AddProductDialogProps {
    businessId: string;
    selectedRetailerId?: string;
    onSelect: (product: CatalogProduct, catalogId: string) => void;
    /** Multi-select: the dialog stays open and every row toggles. */
    multiple?: boolean;
    selectedRetailerIds?: string[];
    /**
     * Pins the catalogue. A multi-product message carries one catalog_id, so once a selection
     * exists its catalogue cannot be switched.
     */
    lockedCatalogId?: string;
}

const mapProduct = (raw: RawMetaProduct): CatalogProduct => ({
    productId: raw.productId || raw.id || "",
    productName: raw.name || "",
    retailerId: raw.retailerId || "",
    description: raw.description || "",
    price: raw.price || "",
    currency: raw.currency,
    availability: raw.availability || "",
    imageUrl: typeof raw.imageUrl === "string" ? raw.imageUrl : "",
    purchaseUrl: raw.purchaseUrl,
});

const AddProductDialog = ({
    businessId,
    selectedRetailerId,
    onSelect,
    multiple = false,
    selectedRetailerIds,
    lockedCatalogId,
}: AddProductDialogProps) => {
    const [catalogs, setCatalogs] = useState<CatalogSummary[]>([]);
    const [selectedCatalogId, setSelectedCatalogId] = useState<string>("");
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const chosen = useMemo(
        () => new Set(multiple ? selectedRetailerIds ?? [] : selectedRetailerId ? [selectedRetailerId] : []),
        [multiple, selectedRetailerIds, selectedRetailerId]
    );

    useEffect(() => {
        if (!businessId) return;
        setLoading(true);
        setError(null);
        axios
            .get(`${environment.baseUrl}/business/get-catalog?businessId=${businessId}`)
            .then((response) => {
                const list: CatalogSummary[] = response.data?.data || [];
                setCatalogs(list);
                if (list.length > 0) {
                    const locked = lockedCatalogId && list.some((c) => c.id === lockedCatalogId)
                        ? lockedCatalogId
                        : null;
                    setSelectedCatalogId(locked ?? list[0].id);
                }
            })
            .catch(() => setError("Failed to load catalogs. Connect your catalog in Dashboard settings first."))
            .finally(() => setLoading(false));
    }, [businessId, lockedCatalogId]);

    const selectedCatalog = catalogs.find((c) => c.id === selectedCatalogId);

    const products = useMemo(() => {
        const raw = selectedCatalog?.metaProducts || [];
        const mapped = raw.map(mapProduct).filter((p) => p.retailerId);
        if (!searchText.trim()) return mapped;
        const q = searchText.toLowerCase();
        return mapped.filter(
            (p) =>
                p.productName.toLowerCase().includes(q) ||
                p.retailerId.toLowerCase().includes(q) ||
                p.description.toLowerCase().includes(q)
        );
    }, [selectedCatalog, searchText]);

    if (loading) {
        return <div style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>Loading products…</div>;
    }

    if (error) {
        return <div style={{ padding: "24px", color: "#dc2626", fontSize: "13px" }}>{error}</div>;
    }

    if (catalogs.length === 0) {
        return (
            <div style={{ padding: "24px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                No catalog connected. Create products in Dashboard → Catalogue first.
            </div>
        );
    }

    return (
        <section style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <select
                    value={selectedCatalogId}
                    disabled={!!lockedCatalogId}
                    title={lockedCatalogId ? "A product list can only use one catalogue" : undefined}
                    onChange={(e) => setSelectedCatalogId(e.target.value)}
                    style={{
                        flex: 1,
                        minWidth: "180px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        fontSize: "13px",
                        background: lockedCatalogId ? "#f1f5f9" : undefined,
                        color: lockedCatalogId ? "#64748b" : undefined,
                    }}
                >
                    {catalogs.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name || c.id}
                        </option>
                    ))}
                </select>
                <input
                    type="text"
                    placeholder="Search products…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{
                        flex: 1,
                        minWidth: "180px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        fontSize: "13px",
                    }}
                />
            </div>

            <div style={{ maxHeight: "360px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
                {products.length === 0 ? (
                    <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                        No products found in this catalog.
                    </div>
                ) : (
                    products.map((product) => {
                        const isSelected = chosen.has(product.retailerId);
                        return (
                            <div
                                key={product.retailerId}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "10px 12px",
                                    borderRadius: "10px",
                                    border: isSelected ? "2px solid var(--primary-bg-color)" : "1px solid #e2e8f0",
                                    background: isSelected ? "#f0fdf4" : "#fff",
                                }}
                            >
                                {product.imageUrl ? (
                                    <img
                                        src={product.imageUrl}
                                        alt={product.productName}
                                        style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 8,
                                            background: "#f1f5f9",
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>
                                        {product.productName}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: 2 }}>
                                        SKU: {product.retailerId}
                                        {product.price ? ` · ${product.price}${product.currency ? ` ${product.currency}` : ""}` : ""}
                                        {product.availability ? ` · ${product.availability}` : ""}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onSelect(product, selectedCatalogId)}
                                    style={{
                                        padding: "6px 14px",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        borderRadius: "8px",
                                        border: "1px solid var(--primary-bg-color)",
                                        background: isSelected ? "var(--primary-bg-color)" : "white",
                                        color: isSelected ? "white" : "var(--primary-bg-color)",
                                        cursor: "pointer",
                                        flexShrink: 0,
                                    }}
                                >
                                    {multiple ? (isSelected ? "Remove" : "Add") : isSelected ? "Selected" : "Select"}
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </section>
    );
};

export default AddProductDialog;
